<template>
  <PageTitle
    v-if="pageTitle"
    :page-title="pageTitle"
  />

  <BannerDisplay
    v-if="banner"
    :banner="banner"
  />

  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100/50 flex overflow-hidden">
    <!-- Narrow sidebar -->
    <div class="hidden w-20 bg-gradient-to-b from-primary via-primary to-indigo-700 overflow-y-auto md:block shadow-xl border-r border-white/10">
      <div class="w-full py-6 flex flex-col items-center relative">
        <!-- Logo section with subtle glow -->
        <div class="flex-shrink-0 flex items-center relative group">
          <div class="absolute inset-0 bg-white/10 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <img
            :src="logo"
            class="h-8 w-auto mb-5 cursor-pointer relative z-10 filter drop-shadow-sm hover:drop-shadow-md transition-all duration-200 transform hover:scale-105"
            :alt="appName"
            @click.prevent="goToWebSiteHome"
          >
        </div>

        <!-- Navigation items -->
        <div class="flex-1 mt-6 w-full px-2 space-y-2">
          <template
            v-for="(item, key) in sidebarNavigation"
            :key="key"
          >
            <Link
              v-if="item.visible"
              :href="item.href"
              :class="[
                item.current
                  ? 'bg-white/20 text-white shadow-lg ring-1 ring-white/30 scale-105'
                  : 'text-indigo-100/80 hover:text-white hover:bg-white/10 hover:scale-105',
                'group w-full p-3 rounded-xl flex flex-col items-center text-xs font-medium transition-all duration-200 relative overflow-hidden'
              ]"
              :aria-current="item.current ? 'page' : undefined"
              :title="item.name"
              :method="item.method"
            >
              <!-- Active indicator -->
              <div
                v-if="item.current"
                class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"
              />

              <component
                :is="item.icon"
                :class="[
                  item.current
                    ? 'text-white drop-shadow-sm'
                    : 'text-indigo-200/70 group-hover:text-white group-hover:drop-shadow-sm',
                  'h-6 w-6 transition-all duration-200'
                ]"
                aria-hidden="true"
              />

              <!-- Tooltip on hover -->
              <div class="absolute left-full ml-4 px-2 py-1 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                {{ item.name }}
                <div class="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
              </div>
            </Link>
          </template>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <TransitionRoot
      as="template"
      :show="mobileMenuOpen"
    >
      <Dialog
        as="div"
        static
        class="md:hidden"
        :open="mobileMenuOpen"
        @close="mobileMenuOpen = false"
      >
        <div class="fixed inset-0 z-40 flex">
          <TransitionChild
            as="template"
            enter="transition-opacity ease-linear duration-300"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay class="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </TransitionChild>

          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <div class="relative max-w-min w-full bg-gradient-to-b from-primary via-primary to-indigo-700 pt-5 pb-4 flex-1 flex flex-col shadow-2xl">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute top-1 right-0 -mr-14 p-1">
                  <button
                    type="button"
                    class="h-12 w-12 rounded-full backdrop-blur-sm bg-white/10 hover:bg-white/20 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200"
                    @click="mobileMenuOpen = false"
                  >
                    <XMarkIcon
                      class="h-6 w-6 text-white drop-shadow-sm"
                      aria-hidden="true"
                    />
                    <span class="sr-only">Close sidebar</span>
                  </button>
                </div>
              </TransitionChild>
              <div class="flex-shrink-0 px-4 flex items-center">
                <img
                  class="h-8 w-auto mb-5 filter drop-shadow-sm"
                  :src="logo"
                  alt="assurdeal"
                >
              </div>
              <div class="mt-5 flex-1 h-0 px-3 overflow-y-auto">
                <nav class="h-full flex flex-col">
                  <div class="space-y-2">
                    <template
                      v-for="(item, key) in sidebarNavigation"
                      :key="key"
                    >
                      <Link
                        v-if="item.visible"
                        :href="item.href"
                        :class="[
                          item.current
                            ? 'bg-white/20 text-white shadow-lg ring-1 ring-white/30'
                            : 'text-indigo-100/80 hover:text-white hover:bg-white/10 hover:shadow-md',
                          'group py-3 px-4 rounded-xl flex items-center text-sm font-medium transition-all duration-200 relative'
                        ]"
                        :aria-current="item.current ? 'page' : undefined"
                        :method="item.method"
                        @click="mobileMenuOpen = false"
                      >
                        <!-- Active indicator -->
                        <div
                          v-if="item.current"
                          class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"
                        />

                        <component
                          :is="item.icon"
                          :class="[
                            item.current
                              ? 'text-white drop-shadow-sm'
                              : 'text-indigo-200/70 group-hover:text-white group-hover:drop-shadow-sm',
                            'mr-3 h-6 w-6 transition-all duration-200'
                          ]"
                          aria-hidden="true"
                        />
                        <span class="truncate">{{ item.name }}</span>
                      </Link>
                    </template>
                  </div>
                </nav>
              </div>
            </div>
          </TransitionChild>
          <div
            class="flex-shrink-0 w-14"
            aria-hidden="true"
          >
            <!-- Dummy element to force sidebar to shrink to fit close icon -->
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Content area -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- NavBar Top -->
      <NavBarTop
        @open-mobile-menu="mobileMenuOpen = true"
        @open-notifications="showNotifications = true"
      />

      <!-- Notifications -->
      <Notifications
        :open="showNotifications"
        @close="showNotifications = false"
      />

      <!-- Main content -->
      <div class="flex-1 flex items-stretch overflow-hidden">
        <main class="flex-1 overflow-y-auto">
          <section
            aria-labelledby="primary-heading"
            class="min-w-0 flex-1 h-full flex flex-col overflow-hidden lg:order-last"
          >
            <UpgradeSubscriptionCTA
              v-if="canUpgradeCurrentSubscriptionPlan"
              class="mx-6 lg:mx-10 mt-4"
              :on-mobile="true"
            />
            <!-- Your content with improved padding and spacing -->
            <div class="flex-1">
              <slot />
            </div>
          </section>
        </main>

        <!-- Secondary column (hidden on smaller screens) -->
        <aside class="hidden lg:block lg:flex-shrink-0 lg:order-first">
          <div class="h-full relative flex flex-col w-96 bg-white/70 backdrop-blur-sm border-r border-gray-200/60 shadow-lg">
            <MyPortfolio
              :progression="portfolioProgression"
            />
            <MyToolBox v-if="anyToolsFeatureEnabled" />
          </div>
        </aside>
      </div>
    </div>

    <ToastNotification />

    <Modal :open="openNotSavedModal">
      <div class="sm:flex sm:items-start">
        <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
          <ExclamationTriangleIcon class="h-6 w-6 text-red-600" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3
            class="text-lg leading-6 font-medium text-gray-900"
            v-text="t('messages.leave_page')"
          />
          <div class="mt-2">
            <p
              class="text-sm text-gray-500"
              v-text="t('messages.confirm_leave_page')"
            />
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <button
          type="button"
          class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
          @click.prevent="navigateAwayFromPage"
          v-text="t('messages.yes')"
        />
        <button
          type="button"
          class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
          @click.prevent="openNotSavedModal = false"
          v-text="t('messages.no')"
        />
      </div>
    </Modal>

    <Modal
      :open="openSubscriptionModal"
      modal-classes="sm:max-w-fit"
    >
      <div class="flex flex-col">
        <ModalCloseButton @click.prevent="hideSubscriptionModal" />

        <section>
          <PricingTable :subscription-plans="subscriptionPlans" />
        </section>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-x-6">
          <button
            class="bg-white rounded-md border border-gray-200 px-3 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            @click.prevent="hideSubscriptionModal"
          >
            <span
              v-text="t('Cancel')"
            />
          </button>
        </div>
      </template>
    </Modal>

    <Modal
      v-if="subscriptionOptInContent"
      :open="showSubscriptionOptInModal"
      modal-classes="sm:max-w-fit"
    >
      <div class="flex flex-col">
        <ModalCloseButton @click.prevent="closeSubscriptionOptInModal" />

        <section>
          <PricingTable
            :title="subscriptionOptInContent.title"
            :subscription-plans="subscriptionPlans"
          >
            <article class="mx-auto prose prose-primary lg:prose-xl mb-8">
              <Alert
                class="text-left"
                type="success"
                :message="subscriptionOptInContent.htmlBody"
              />
            </article>
          </PricingTable>
          <div class="py-8 px-4 mx-auto max-w-screen-xl lg:px-6">
            <button
              class="text-white w-full bg-primary hover:bg-primary-700 disabled:bg-white disabled:text-gray-300 disabled:cursor-not-allowed disabled:border-2 disabled:border-gray-200 focus:ring-4 focus:ring-primary-200 focus:ring-primary-900 font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-50"
              @click.prevent="closeSubscriptionOptInModal"
              v-text="t('Decide Later')"
            />
          </div>
        </section>
      </div>
    </Modal>
  </div>
</template>

<script lang="ts" setup>
import { provide, computed, Ref, ref, watch } from 'vue';
import {
  Dialog,
  DialogOverlay,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import {
  XMarkIcon,
  UserIcon,
  CalculatorIcon,
  SquaresPlusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/vue/24/outline';
import collect from 'collect.js';
import { useI18n } from 'vue-i18n';
import Modal from '@/Components/Modal.vue';
import Alert from '@/Components/Alert.vue';
import logo from '@/assets/images/logo-mark.png';
import { SideBarNavItem } from '@/types/common';
import useAppData from '@/composables/useAppData';
import NavBarTop from '@/Components/NavBarTop.vue';
import MyToolBox from '@/Components/MyToolBox.vue';
import PageTitle from '@/Components/PageTitle.vue';
import MyPortfolio from '@/Components/MyPortfolio.vue';
import { usePage, Link, router } from '@inertiajs/vue3';
import Notifications from '@/Components/Notifications.vue';
import { usePageTitle } from '@/composables/usePageTitle';
import BannerDisplay from '@/Components/BannerDisplay.vue';
import { proceed } from '@/composables/useTrackModifications';
import useInitStoreData from '@/composables/useInitStoreData';
import useBackendRouting from '@/composables/useBackendRouting';
import ToastNotification from '@/Components/ToastNotification.vue';
import ModalCloseButton from '@/Components/UI/ModalCloseButton.vue';
import PricingTable from '@/Components/Subscription/PricingTable.vue';
import UpgradeSubscriptionCTA from '@/Components/Subscription/UpgradeSubscriptionCTA.vue';

useInitStoreData().execute();

const { t } = useI18n();
const { route } = useBackendRouting();

const portfolioProgression = computed(() => usePage().props.portfolioProgression);
const subscriptionPlans = computed(() => usePage().props.subscriptionPlans as any);
const showSubscriptionOptIn = computed(() => usePage().props.showSubscriptionOptIn as boolean);
const subscriptionOptInContent = computed(() => usePage().props.subscriptionOptInContent as any);

const {
  appName,
  disableChatbot,
  anyToolsFeatureEnabled,
  canUpgradeCurrentSubscriptionPlan,
} = useAppData();

const {
  pageTitle,
} = usePageTitle();

// Side Navigation
const sidebarNavigation: Ref<{ [key: string]: SideBarNavItem }> = ref({
  dashboard: {
    name: t('messages.dashboard'),
    href: route('user.dashboard'),
    icon: SquaresPlusIcon,
    current: false,
    method: 'get',
    visible: true,
    evaluateCurrent: (item: SideBarNavItem) => {
      item.current = window.location.href === route('user.dashboard');
    },
  },
  my_tools: {
    name: t('messages.tool', 2),
    href: route('broker_dashboard.tools.index'),
    icon: CalculatorIcon,
    current: false,
    method: 'get',
    visible: anyToolsFeatureEnabled.value,
    evaluateCurrent: (item) => {
      item.current = window.location.href.includes(t('routes.tools'));
    },
  },
  my_account: {
    name: t('messages.my_account'),
    href: route('user.dashboard.profile'),
    icon: UserIcon,
    current: false,
    method: 'get',
    visible: true,
    evaluateCurrent: (item) => {
      item.current = window.location.href === route('user.dashboard.profile');
    },
  },
});

const mobileMenuOpen = ref(false);
const showNotifications = ref(false);

const openNotSavedModal: Ref<boolean> = ref(false);
const openSubscriptionModal: Ref<boolean> = ref(false);
const showSubscriptionAlert: Ref<boolean> = ref(false);
const showSubscriptionOptInModal: Ref<boolean> = ref(showSubscriptionOptIn);

const banner = computed(() => usePage().props.banner);

/**
 * Lose page data and navigate away.
 */
function navigateAwayFromPage() {
  openNotSavedModal.value = false;
  proceed();
}

/**
 * Go back to homepage.
 */
function goToWebSiteHome() {
  window.location.href = route('homepage');
}

/**
 * Show Subscription modal.
 */
function showSubscriptionModal() {
  openSubscriptionModal.value = true;
}

/**
 * Hide Subscription modal.
 */
function hideSubscriptionModal() {
  openSubscriptionModal.value = false;
}

/**
 * Close Subscription opt-in modal.
 */
function closeSubscriptionOptInModal() {
  showSubscriptionOptInModal.value = false;

  router.put(route('broker_dashboard.subscription_opt_in_acknowledgment'));
}

/**
 * Set current tab on sidebar.
 */
function setCurrentSideBarTab() {
  collect(sidebarNavigation.value)
    .each((item: SideBarNavItem) => item.evaluateCurrent(item));
}
setCurrentSideBarTab();
router.on('success', () => setCurrentSideBarTab());
router.on('invalid', (event) => {

  const response = event.detail.response;
  const status = response?.status;
  const code = response.data?.code;

  if (status === 403) {
    //   @see SubscriptionErrorCode
    if (code === 10002 || code === 10001) {
      showSubscriptionAlert.value = true;
      openSubscriptionModal.value = true;
      event.preventDefault();
    }
  }
});

provide('registerTracking', {
  openNotSavedModal,
});

provide('subscriptionModal', {
  openSubscriptionModal,
  showSubscriptionModal,
});

watch(() => disableChatbot.value, (newValue) => {
  if (newValue) {
    document.dispatchEvent(new CustomEvent('removeChatbot'));
  } else {
    document.dispatchEvent(new CustomEvent('initChatbot'));
  }
});
</script>
